import { IconButton } from "../IconButton/IconButton";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import {
	actionComponentFactory,
	computeActionsToRender
} from "../ActionList/actionsRenderHelpers";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import {
	Action,
	ActionAndOthers,
	ActionKind,
	resolveActionTitle
} from "client_legacy/ndaw/actions";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import {
	ReactElement,
	useRef,
	useMemo,
	Fragment,
	RefObject
} from "react";

export type QuickActionsProps<TActionContext> = {
	isTouch: boolean;
	actions: readonly ActionAndOthersWithQuickActionIndex<TActionContext>[];
	actionContext: TActionContext;
	isMoreActionsOpen: boolean;
	onIsMoreActionOpenChange(isMoreActionOpen: boolean): void;
	maxActionCount?: number;
	menuTitle: string;
};

export const QuickActions = <TActionContext,>(props: QuickActionsProps<TActionContext>): ReactElement => {
	const {
		actions,
		actionContext,
		isTouch,
		isMoreActionsOpen,
		onIsMoreActionOpenChange,
		maxActionCount,
		menuTitle
	} = props;

	const actionsMenuButton: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const availableActions = useMemo(
		(): readonly ActionAndOthersWithQuickActionIndex<TActionContext>[] => {
			return computeActionsToRender(
				// "manual" dividers are not rendered, the dividers get automatically injected
				actions.filter(x => x.kind !== ActionKind.Divider),
				actionContext,
				true
			);
		},
		[actions, actionContext]
	);

	const quickActions = useMemo(
		(): readonly ActionAndOthersWithQuickActionIndex<TActionContext>[] => {
			if(isTouch) {
				return [];
			}

			const sortedActions: ActionAndOthersWithQuickActionIndex<TActionContext>[] = sortBy(
				availableActions.filter(
					x => isNotNil(x.quickActionIndex)
				),
				x => x.quickActionIndex
			);

			return isNotNil(maxActionCount)
				? sortedActions.slice(0, maxActionCount)
				: sortedActions;
		},
		[
			isTouch,
			availableActions,
			maxActionCount
		]
	);

	return (
		<>
			{
				quickActions.map(
					x => (
						<ActionButton
							key={x.name}
							action={x}
							actionContext={actionContext}
						/>
					)
				)
			}
			{availableActions.length > 0 && (
				<>
					<IconButton
						trackingId="more-button"
						icon="ellipsis"
						kind="transparent"
						onClick={() => onIsMoreActionOpenChange(true)}
						bubbleClick={false}
						ref={actionsMenuButton}
						isActive={isMoreActionsOpen}
						shape="round"
					/>
					{
						isMoreActionsOpen && actionsMenuButton.current && (
							<DropOverActionList
								trackingId={generateTrackingId("quick-actions-more-button", "__dropover")}
								target={actionsMenuButton.current}
								targetAlignment="br"
								contentAlignment="tr"
								onClose={(): void => onIsMoreActionOpenChange(false)}
								bubbleClick={false}
								bubbleContextMenu={false}
								menuTitle={menuTitle}
							>
								{availableActions.map((x) => (
									<Fragment key={x.name}>
										{actionComponentFactory(x, actionContext)}
									</Fragment>
								))}
							</DropOverActionList>
						)
					}
				</>
			)}
		</>
	);
};
QuickActions.displayName = "QuickActions";

type QuickActionIndex = {
	quickActionIndex?: number;
};

export type ActionWithQuickActionIndex<TActionContext> = Action<TActionContext> & QuickActionIndex;
export type ActionAndOthersWithQuickActionIndex<TActionContext> = ActionAndOthers<TActionContext> & QuickActionIndex;

export type ActionButtonProps<TActionContext> = {
	action: ActionAndOthersWithQuickActionIndex<TActionContext>;
	actionContext: TActionContext;
};

export const ActionButton = <TActionContext,>(props: ActionButtonProps<TActionContext>): ReactElement => {
	const {
		action,
		actionContext
	} = props;

	const {
		name,
		title,
		icon,
		execute
	} = action;

	return (
		<IconButton
			trackingId={name}
			icon={ensureNotNil(icon)}
			kind="transparent"
			bubbleClick={false}
			onClick={() => execute?.(actionContext)}
			tooltip={title
				? resolveActionTitle(title, actionContext)
				: undefined
			}
			shape="round"
		/>
	);
};
ActionButton.displayName = "ActionButton";